import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "frb" }
const _hoisted_2 = { class: "frb__card" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = {
  key: 0,
  class: "frb__error"
}
const _hoisted_5 = {
  key: 1,
  class: "frb__list"
}
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "frb__name" }
const _hoisted_8 = { class: "frb__handle" }
const _hoisted_9 = ["disabled", "onClick"]
const _hoisted_10 = {
  key: 1,
  class: "frb__meta"
}
const _hoisted_11 = {
  key: 2,
  class: "frb__error"
}
const _hoisted_12 = {
  key: 3,
  class: "frb__empty"
}
const _hoisted_13 = {
  key: 4,
  class: "frb__empty"
}
const _hoisted_14 = {
  key: 5,
  class: "frb__list"
}
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "frb__name" }
const _hoisted_17 = { class: "frb__handle" }
const _hoisted_18 = { class: "frb__actions" }
const _hoisted_19 = ["disabled", "onClick"]
const _hoisted_20 = ["disabled", "onClick"]
const _hoisted_21 = {
  key: 6,
  class: "frb__empty"
}
const _hoisted_22 = {
  key: 7,
  class: "frb__list"
}
const _hoisted_23 = ["onClick"]
const _hoisted_24 = { class: "frb__name" }
const _hoisted_25 = { class: "frb__handle" }
const _hoisted_26 = ["disabled", "onClick"]

import { onBeforeUnmount, onMounted, reactive, ref } from "vue";
import {
  acceptFriendRequest,
  declineFriendRequest,
  getFriendsOverview,
  getPublicProfile,
  searchSocialProfiles,
  sendFriendRequest,
  sendSpeakingInvite,
} from "@/api/social";
import {
  createSpeakingRoom,
  listActiveSpeakingRooms,
  openSpeakingLobby,
  SpeakingRoomExistsError,
} from "@/api/speakingRooms";
import PublicProfileModal from "@/components/PublicProfileModal.vue";
import { handleError } from "@/services/errorHandler";
import type { FriendsOverview, PublicProfile, PublicUser } from "@/types";

const POLL_MS = 8000;


export default /*@__PURE__*/_defineComponent({
  __name: 'FriendshipBox',
  setup(__props) {

const query = ref("");
const searching = ref(false);
const searchError = ref<string | null>(null);
const actionError = ref<string | null>(null);
const results = ref<PublicProfile[]>([]);
const loading = ref(true);
const busy = ref(false);
const openProfileData = ref<PublicProfile | null>(null);
const overview = reactive<FriendsOverview>({
  incoming: [],
  outgoing: [],
  friends: [],
});

let pollId: ReturnType<typeof setInterval> | null = null;
let searchTimer: ReturnType<typeof setTimeout> | null = null;

function personName(user: PublicUser): string {
  return user.full_name?.trim() || user.username;
}

function statusShort(status: PublicProfile["friendship_status"]): string {
  if (status === "friends") return "دوست";
  if (status === "outgoing") return "ارسال‌شده";
  if (status === "incoming") return "درخواست";
  return "";
}

function onQueryInput() {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    if (query.value.length >= 2) void runSearch();
    else results.value = [];
  }, 280);
}

async function runSearch() {
  if (query.value.length < 2) return;
  searching.value = true;
  searchError.value = null;
  try {
    results.value = await searchSocialProfiles(query.value);
  } catch (e) {
    searchError.value = handleError(e, "FriendshipBox.search");
  } finally {
    searching.value = false;
  }
}

async function loadOverview(silent = false) {
  if (!silent) loading.value = true;
  try {
    const data = await getFriendsOverview();
    overview.incoming = data.incoming;
    overview.outgoing = data.outgoing;
    overview.friends = data.friends;
  } catch (e) {
    if (!silent) actionError.value = handleError(e, "FriendshipBox.overview");
  } finally {
    loading.value = false;
  }
}

async function openProfile(username: string) {
  actionError.value = null;
  try {
    openProfileData.value = await getPublicProfile(username);
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.profile");
  }
}

async function sendRequest(username: string) {
  busy.value = true;
  actionError.value = null;
  try {
    await sendFriendRequest(username);
    await Promise.all([loadOverview(true), query.value ? runSearch() : Promise.resolve()]);
    if (openProfileData.value?.user.username === username) {
      openProfileData.value = await getPublicProfile(username);
    }
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.request");
  } finally {
    busy.value = false;
  }
}

async function accept(requestId: number) {
  busy.value = true;
  actionError.value = null;
  try {
    await acceptFriendRequest(requestId);
    await loadOverview(true);
    if (openProfileData.value) {
      openProfileData.value = await getPublicProfile(openProfileData.value.user.username);
    }
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.accept");
  } finally {
    busy.value = false;
  }
}

async function decline(requestId: number) {
  busy.value = true;
  actionError.value = null;
  try {
    await declineFriendRequest(requestId);
    await loadOverview(true);
    openProfileData.value = null;
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.decline");
  } finally {
    busy.value = false;
  }
}

async function ensureRoomCode(): Promise<string> {
  const rooms = await listActiveSpeakingRooms();
  const mine = rooms.find((room) => room.is_mine);
  if (mine) return mine.room_code;
  try {
    const created = await createSpeakingRoom();
    return created.room_code;
  } catch (e) {
    if (e instanceof SpeakingRoomExistsError) return e.roomCode;
    throw e;
  }
}

async function invite(username: string) {
  busy.value = true;
  actionError.value = null;
  try {
    const code = await ensureRoomCode();
    await sendSpeakingInvite(username, code);
    openSpeakingLobby(code, "examiner");
    openProfileData.value = null;
  } catch (e) {
    actionError.value = handleError(e, "FriendshipBox.invite");
  } finally {
    busy.value = false;
  }
}

onMounted(() => {
  void loadOverview();
  pollId = setInterval(() => {
    void loadOverview(true);
  }, POLL_MS);
});

onBeforeUnmount(() => {
  if (pollId) clearInterval(pollId);
  if (searchTimer) clearTimeout(searchTimer);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[2] || (_cache[2] = _createElementVNode("header", { class: "frb__header" }, [
        _createElementVNode("i", {
          class: "bi bi-people-fill frb__header-icon",
          "aria-hidden": "true"
        }),
        _createElementVNode("div", null, [
          _createElementVNode("h2", { class: "frb__title" }, "درخواست دوستی"),
          _createElementVNode("p", { class: "frb__subtitle" }, "جستجو کنید، پروفایل ببینید، بعد دوست شوید")
        ])
      ], -1)),
      _createElementVNode("form", {
        class: "frb__search",
        onSubmit: _withModifiers(runSearch, ["prevent"])
      }, [
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
          class: "frb__input",
          type: "search",
          maxlength: "24",
          placeholder: "نام کاربری را بنویسید",
          autocomplete: "off",
          onInput: onQueryInput
        }, null, 544), [
          [
            _vModelText,
            query.value,
            void 0,
            { trim: true }
          ]
        ]),
        _createElementVNode("button", {
          type: "submit",
          class: "frb__search-btn",
          disabled: searching.value || query.value.length < 2
        }, " جستجو ", 8, _hoisted_3)
      ], 32),
      (searchError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(searchError.value), 1))
        : _createCommentVNode("", true),
      (results.value.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(results.value, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: item.user.id,
                class: "frb__row"
              }, [
                _createElementVNode("button", {
                  type: "button",
                  class: "frb__person",
                  onClick: ($event: any) => (openProfile(item.user.username))
                }, [
                  _createElementVNode("span", _hoisted_7, _toDisplayString(personName(item.user)), 1),
                  _createElementVNode("span", _hoisted_8, "@" + _toDisplayString(item.user.username), 1)
                ], 8, _hoisted_6),
                (item.friendship_status === 'none')
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      type: "button",
                      class: "frb__join",
                      disabled: busy.value,
                      onClick: ($event: any) => (sendRequest(item.user.username))
                    }, " درخواست ", 8, _hoisted_9))
                  : (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(statusShort(item.friendship_status)), 1))
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (actionError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(actionError.value), 1))
        : _createCommentVNode("", true),
      _cache[3] || (_cache[3] = _createElementVNode("h3", { class: "frb__section" }, "درخواست‌های رسیده", -1)),
      (loading.value && !overview.incoming.length)
        ? (_openBlock(), _createElementBlock("p", _hoisted_12, "در حال بارگذاری…"))
        : (!overview.incoming.length)
          ? (_openBlock(), _createElementBlock("p", _hoisted_13, "درخواست معلقی ندارید."))
          : (_openBlock(), _createElementBlock("ul", _hoisted_14, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(overview.incoming, (req) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: req.id,
                  class: "frb__row"
                }, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "frb__person",
                    onClick: ($event: any) => (openProfile(req.from_user.username))
                  }, [
                    _createElementVNode("span", _hoisted_16, _toDisplayString(personName(req.from_user)), 1),
                    _createElementVNode("span", _hoisted_17, "@" + _toDisplayString(req.from_user.username), 1)
                  ], 8, _hoisted_15),
                  _createElementVNode("div", _hoisted_18, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "frb__join",
                      disabled: busy.value,
                      onClick: ($event: any) => (accept(req.id))
                    }, " پذیرش ", 8, _hoisted_19),
                    _createElementVNode("button", {
                      type: "button",
                      class: "frb__remove",
                      disabled: busy.value,
                      onClick: ($event: any) => (decline(req.id))
                    }, " رد ", 8, _hoisted_20)
                  ])
                ]))
              }), 128))
            ])),
      _cache[4] || (_cache[4] = _createElementVNode("h3", { class: "frb__section" }, "دوستان", -1)),
      (!overview.friends.length)
        ? (_openBlock(), _createElementBlock("p", _hoisted_21, "هنوز دوستی ندارید."))
        : (_openBlock(), _createElementBlock("ul", _hoisted_22, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(overview.friends, (row) => {
              return (_openBlock(), _createElementBlock("li", {
                key: row.user.id,
                class: "frb__row"
              }, [
                _createElementVNode("button", {
                  type: "button",
                  class: "frb__person",
                  onClick: ($event: any) => (openProfile(row.user.username))
                }, [
                  _createElementVNode("span", _hoisted_24, _toDisplayString(personName(row.user)), 1),
                  _createElementVNode("span", _hoisted_25, "@" + _toDisplayString(row.user.username), 1)
                ], 8, _hoisted_23),
                _createElementVNode("button", {
                  type: "button",
                  class: "frb__join",
                  disabled: busy.value,
                  title: "دعوت به اسپیکینگ",
                  onClick: ($event: any) => (invite(row.user.username))
                }, " اسپیکینگ ", 8, _hoisted_26)
              ]))
            }), 128))
          ]))
    ]),
    _createVNode(PublicProfileModal, {
      profile: openProfileData.value,
      busy: busy.value,
      onClose: _cache[1] || (_cache[1] = ($event: any) => (openProfileData.value = null)),
      onRequest: sendRequest,
      onAccept: accept,
      onDecline: decline,
      onInvite: invite
    }, null, 8, ["profile", "busy"])
  ]))
}
}

})